// ─────────────────────────────────────────────────────────────────
// Glass-i — Inventory Inward Posting helpers (Stage 6P.5).
//
// Pure ESM module — exercised by the frontend node:test runner.
//
// Frontend mirror of backend Inventory Inward math + payload
// shaping. The backend
// (`backend/src/services/erp/inventory/inventoryCalculation.service.js`
// and `inventoryPosting.service.js`) remains the source of truth for:
//   - posted qty / sqmt / sqft / total_length
//   - movement_no allocation (INW-YYYYMMDD-NNNNNN)
//   - duplicate-block decisions
//   - skip rules for SERVICE / JOBWORK
//   - reference valuation (rate × qty)
//
// These helpers exist only so the Stage 6P.5 inward preview / post UI
// can show consistent numbers, badges and capability gates while the
// operator reviews the preview. The API recomputes on post and is the
// final authority.
//
// Mirrors the style of:
//   - frontend/src/pages/erp/purchase/purchaseBillHelpers.js
//     (option lists, normalize/build, capability gates, status colour
//     palette, tolerant numeric coercion).
//   - backend/src/services/erp/inventory/inventory.constants.js
//     (enum lists — frontend mirrors are sealed with Object.freeze so
//     callers can't mutate a shared array by accident).
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// Enum mirrors — kept aligned with backend
// services/erp/inventory/inventory.constants.js.
// Frozen so callers can't mutate the shared lists by accident.
// ─────────────────────────────────────────────────────────────────

export const ITEM_TYPES = Object.freeze([
  'GLASS',
  'HARDWARE',
  'ACCESSORY',
  'PROFILE',
  'UPVC',
  'ALUMINIUM',
  'SERVICE',
  'JOBWORK',
  'OTHER'
]);

// Length-based UOMs — RFT / RMT / MTR drive per-length math.
export const LENGTH_BASED_UOMS = Object.freeze(['RFT', 'RMT', 'MTR']);

// Profile family — sub-set of item types whose lines are driven by
// length × pieces math (PROFILE / UPVC / ALUMINIUM share a code path).
export const PROFILE_FAMILY_TYPES = Object.freeze(['PROFILE', 'UPVC', 'ALUMINIUM']);

// Glass family — sub-set of item types whose lines run through the
// sqmt ↔ sqft area conversion path.
export const GLASS_FAMILY_TYPES = Object.freeze(['GLASS']);

// Simple-quantity family — item types whose ledger rows are plain
// qty (no area, no per-length conversion).
export const SIMPLE_QTY_TYPES = Object.freeze([
  'HARDWARE',
  'ACCESSORY',
  'OTHER'
]);

// Non-stockable family — SERVICE / JOBWORK skip the inward posting
// engine entirely (no ledger row, no current-stock impact).
export const NON_STOCKABLE_TYPES = Object.freeze(['SERVICE', 'JOBWORK']);

// Movement / source / status enums — mirror the backend
// inventory.constants exports of the same name.
export const MOVEMENT_TYPES = Object.freeze([
  'PURCHASE_INWARD',
  'PURCHASE_INWARD_REVERSAL',
  'ADJUSTMENT'
]);

export const SOURCE_TYPES = Object.freeze(['GRN', 'PURCHASE_BILL']);

export const POSTED_STATUSES = Object.freeze([
  'POSTED',
  'REVERSED',
  'CANCELLED'
]);

export const VALUATION_STATUSES = Object.freeze([
  'NOT_POSTED',
  'VALUATION_PENDING',
  'VALUED'
]);

export const EVENT_TYPES = Object.freeze([
  'POSTED',
  'SKIPPED_NON_STOCKABLE',
  'DUPLICATE_BLOCKED',
  'REVERSAL_RESERVED',
  'ERROR'
]);

// mm² → m² divisor — keep symbolic so it reads at the call site.
export const SQMT_DIVISOR = 1000000;

// 1 SQMT = 10.764 SQFT — matches backend SQFT_PER_SQMT.
export const SQFT_PER_SQMT = 10.764;


// ─────────────────────────────────────────────────────────────────
// Private constants used by the math + classifier helpers.
// ─────────────────────────────────────────────────────────────────

const GLASS_TYPES_SET   = new Set(GLASS_FAMILY_TYPES);
const PROFILE_TYPES_SET = new Set(PROFILE_FAMILY_TYPES);
const SIMPLE_TYPES_SET  = new Set(SIMPLE_QTY_TYPES);
const NON_STOCKABLE_SET = new Set(NON_STOCKABLE_TYPES);
const KNOWN_TYPES_SET   = new Set(ITEM_TYPES);
const LENGTH_UOMS_SET   = new Set(LENGTH_BASED_UOMS);

const MOVEMENT_TYPES_SET = new Set(MOVEMENT_TYPES);
const SOURCE_TYPES_SET   = new Set(SOURCE_TYPES);
const POSTED_STATUSES_SET = new Set(POSTED_STATUSES);

const ACTION_POST              = 'POST';
const ACTION_DUPLICATE_BLOCKED = 'DUPLICATE_BLOCKED';
const ACTION_SKIP_NON_STOCKABLE = 'SKIP_NON_STOCKABLE';


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric coercion — mirrors backend toNumber.
// Null / undefined / empty string / non-finite all collapse to 0.
// Never throws, never returns NaN.
// ─────────────────────────────────────────────────────────────────

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Half-up rounding — defeats IEEE-754 surprises like 1.005 → 1.0049…
function roundHalfUp(value, decimals) {
  const n = toNumber(value);
  if (n === 0) return 0;
  const factor = Math.pow(10, decimals);
  const sign   = n < 0 ? -1 : 1;
  const abs    = Math.abs(n);
  const scaled = abs * factor + 1e-9;
  return (sign * Math.round(scaled)) / factor;
}

function round4(n) { return roundHalfUp(n, 4); }


// ─────────────────────────────────────────────────────────────────
// Snake / camel access — preview rows arrive from the API in camel,
// but ledger / current-stock rows arrive in both depending on
// whether they were freshly mapped or replayed from a server cache.
// We probe both and never mutate the caller.
// ─────────────────────────────────────────────────────────────────

function pick(obj, snake, camel, fallback) {
  if (!obj || typeof obj !== 'object') return fallback;
  if (snake in obj && obj[snake] !== undefined) return obj[snake];
  if (camel in obj && obj[camel] !== undefined) return obj[camel];
  return fallback;
}

function pickStringUpper(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim().toUpperCase();
}


// ─────────────────────────────────────────────────────────────────
// Preview math — drives the per-line cells in the inward preview
// table. Mirrors the backend inventoryCalculation.service formulas:
//
//   sqmt          = round4(widthMm × heightMm × qty ÷ 1,000,000)
//   sqft          = round4(sqmt × 10.764)
//   totalLength   = round4(lengthValue × qty)
//
// All three return 0 on missing / non-positive input — the backend
// rejects on post, but the UI shows 0 so a row never crashes the
// preview grid.
// ─────────────────────────────────────────────────────────────────

export function calculatePreviewSqmt(widthMm, heightMm, qty) {
  const w = toNumber(widthMm);
  const h = toNumber(heightMm);
  const q = toNumber(qty);
  if (w <= 0 || h <= 0 || q <= 0) return 0;
  return round4((w * h * q) / SQMT_DIVISOR);
}

export function calculatePreviewSqft(sqmt) {
  const s = toNumber(sqmt);
  if (s <= 0) return 0;
  return round4(s * SQFT_PER_SQMT);
}

export function calculatePreviewTotalLength(lengthValue, qty) {
  const l = toNumber(lengthValue);
  const q = toNumber(qty);
  if (l <= 0 || q <= 0) return 0;
  return round4(l * q);
}


// ─────────────────────────────────────────────────────────────────
// Row normalisers — the API returns camelCase rows already, but the
// grid + drawer also consume rows replayed from local state or
// fixture data that may carry either casing. These normalisers return
// a canonical camelCase shape with widened numbers, never mutating
// the input.
//
// Anything we don't recognise is preserved on the way out so the
// grid can show columns we don't know about (e.g. future tenant
// extensions) without losing data.
// ─────────────────────────────────────────────────────────────────

export function normalizeStockLedgerRow(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.id                  = pick(row, 'id', 'id', null);
  out.movementNo          = pick(row, 'movement_no',           'movementNo',          null);
  out.movementDate        = pick(row, 'movement_date',         'movementDate',        null);
  out.movementType        = pick(row, 'movement_type',         'movementType',        null);
  out.sourceType          = pick(row, 'source_type',           'sourceType',          null);
  out.sourceId            = pick(row, 'source_id',             'sourceId',            null);
  out.sourceNo            = pick(row, 'source_no',             'sourceNo',            null);
  out.sourceLineId        = pick(row, 'source_line_id',        'sourceLineId',        null);

  out.grnId               = pick(row, 'grn_id',                'grnId',               null);
  out.grnLineId           = pick(row, 'grn_line_id',           'grnLineId',           null);
  out.purchaseBillId      = pick(row, 'purchase_bill_id',      'purchaseBillId',      null);
  out.purchaseBillLineId  = pick(row, 'purchase_bill_line_id', 'purchaseBillLineId',  null);
  out.purchaseOrderId     = pick(row, 'purchase_order_id',     'purchaseOrderId',     null);
  out.purchaseOrderLineId = pick(row, 'purchase_order_line_id','purchaseOrderLineId', null);

  out.itemId              = pick(row, 'item_id',         'itemId',         null);
  out.itemType            = pickStringUpper(row, 'item_type', 'itemType') || null;
  out.itemName            = pick(row, 'item_name',       'itemName',       null);
  out.itemDesc            = pick(row, 'item_desc',       'itemDesc',       null);
  out.stockName           = pick(row, 'stock_name',      'stockName',      null);
  out.itemDescription     = pick(row, 'item_description','itemDescription',null);

  out.thicknessMm         = toNumber(pick(row, 'thickness_mm', 'thicknessMm', 0)) || 0;
  out.widthMm             = toNumber(pick(row, 'width_mm',     'widthMm',     0)) || 0;
  out.heightMm            = toNumber(pick(row, 'height_mm',    'heightMm',    0)) || 0;

  out.qty                 = toNumber(pick(row, 'qty',          'qty',          0));
  out.sqmt                = toNumber(pick(row, 'sqmt',         'sqmt',         0));
  out.sqft                = toNumber(pick(row, 'sqft',         'sqft',         0));
  out.lengthValue         = toNumber(pick(row, 'length_value', 'lengthValue',  0));
  out.lengthUom           = pick(row, 'length_uom',  'lengthUom',  null);
  out.totalLength         = toNumber(pick(row, 'total_length', 'totalLength',  0));
  out.uom                 = pick(row, 'uom',         'uom',         null);

  out.warehouseId         = pick(row, 'warehouse_id',   'warehouseId',   null);
  out.warehouseName       = pick(row, 'warehouse_name', 'warehouseName', null);
  out.rackId              = pick(row, 'rack_id',        'rackId',        null);
  out.rackName            = pick(row, 'rack_name',      'rackName',      null);
  out.batchNo             = pick(row, 'batch_no',       'batchNo',       null);
  out.lotNo               = pick(row, 'lot_no',         'lotNo',         null);

  out.referenceRate       = toNumber(pick(row, 'reference_rate',   'referenceRate',   0));
  out.referenceAmount     = toNumber(pick(row, 'reference_amount', 'referenceAmount', 0));
  out.valuationStatus     = pickStringUpper(row, 'valuation_status', 'valuationStatus') || null;

  out.postedStatus        = pickStringUpper(row, 'posted_status', 'postedStatus') || null;
  out.postedBy            = pick(row, 'posted_by',          'postedBy',          null);
  out.postedByName        = pick(row, 'posted_by_name',     'postedByName',      null);
  out.postedByUsername    = pick(row, 'posted_by_username', 'postedByUsername',  null);
  out.postedAt            = pick(row, 'posted_at',          'postedAt',          null);
  out.reversalOfLedgerId  = pick(row, 'reversal_of_ledger_id', 'reversalOfLedgerId', null);
  out.remarks             = pick(row, 'remarks', 'remarks', null);

  return out;
}

export function normalizeCurrentStockRow(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.id              = pick(row, 'id', 'id', null);
  out.itemId          = pick(row, 'item_id',     'itemId',     null);
  out.itemType        = pickStringUpper(row, 'item_type', 'itemType') || null;
  out.itemName        = pick(row, 'item_name',   'itemName',   null);
  out.itemDesc        = pick(row, 'item_desc',   'itemDesc',   null);
  out.stockName       = pick(row, 'stock_name',  'stockName',  null);

  out.thicknessMm     = toNumber(pick(row, 'thickness_mm', 'thicknessMm', 0)) || 0;
  out.widthMm         = toNumber(pick(row, 'width_mm',     'widthMm',     0)) || 0;
  out.heightMm        = toNumber(pick(row, 'height_mm',    'heightMm',    0)) || 0;
  out.lengthValue     = toNumber(pick(row, 'length_value', 'lengthValue', 0));
  out.lengthUom       = pick(row, 'length_uom', 'lengthUom', null);

  out.warehouseId     = pick(row, 'warehouse_id',   'warehouseId',   null);
  out.warehouseName   = pick(row, 'warehouse_name', 'warehouseName', null);
  out.rackId          = pick(row, 'rack_id',        'rackId',        null);
  out.rackName        = pick(row, 'rack_name',      'rackName',      null);
  out.batchNo         = pick(row, 'batch_no',       'batchNo',       null);
  out.lotNo           = pick(row, 'lot_no',         'lotNo',         null);

  out.qtyBalance         = toNumber(pick(row, 'qty_balance',          'qtyBalance',          0));
  out.sqmtBalance        = toNumber(pick(row, 'sqmt_balance',         'sqmtBalance',         0));
  out.sqftBalance        = toNumber(pick(row, 'sqft_balance',         'sqftBalance',         0));
  out.totalLengthBalance = toNumber(pick(row, 'total_length_balance', 'totalLengthBalance',  0));
  out.uom                = pick(row, 'uom', 'uom', null);

  out.lastMovementId  = pick(row, 'last_movement_id', 'lastMovementId', null);
  out.lastMovementAt  = pick(row, 'last_movement_at', 'lastMovementAt', null);

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Preview normaliser — defangs missing / wrong-shape fields from the
// `/inward/preview/...` endpoints. The shape mirrors
// inventoryPosting.service.previewInventoryInwardFromGrn /
// previewInventoryInwardFromPurchaseBill.
//
// Defensive: ignores non-array candidates, recomputes counts from the
// classifier action so the summary card always lines up with the
// candidate list (the backend recomputes too, but a stale cache or
// optimistic local edit would otherwise show conflicting numbers).
// ─────────────────────────────────────────────────────────────────

export function normalizeInventoryPostingPreview(preview) {
  if (!preview || typeof preview !== 'object') {
    return {
      source: null,
      sourceHeader: null,
      candidates: [],
      postableCount: 0,
      blockedCount: 0,
      skippedCount: 0
    };
  }
  const sourceRaw = pickStringUpper(preview, 'source', 'source');
  const source    = SOURCE_TYPES_SET.has(sourceRaw) ? sourceRaw : (sourceRaw || null);
  const sourceHeader = preview.sourceHeader && typeof preview.sourceHeader === 'object'
    ? Object.assign({}, preview.sourceHeader)
    : null;

  const rawCandidates = Array.isArray(preview.candidates) ? preview.candidates : [];
  const candidates = rawCandidates
    .filter((c) => c && typeof c === 'object')
    .map((c) => {
      const action = pickStringUpper(c, 'action', 'action') || null;
      return Object.assign({}, c, {
        action,
        existingLedgerId: pick(c, 'existing_ledger_id', 'existingLedgerId', null),
        sourceLineId:     pick(c, 'source_line_id',     'sourceLineId',     null),
        itemId:           pick(c, 'item_id',            'itemId',           null),
        itemType:         pickStringUpper(c, 'item_type', 'itemType') || null,
        itemName:         pick(c, 'item_name',          'itemName',         null),
        qty:              toNumber(pick(c, 'qty',          'qty',          0)),
        sqmt:             toNumber(pick(c, 'sqmt',         'sqmt',         0)),
        sqft:             toNumber(pick(c, 'sqft',         'sqft',         0)),
        totalLength:      toNumber(pick(c, 'total_length', 'totalLength',  0)),
        uom:              pickStringUpper(c, 'uom', 'uom') || null,
        warehouseId:      pick(c, 'warehouse_id', 'warehouseId', null),
        referenceRate:    toNumber(pick(c, 'reference_rate',   'referenceRate',   0)),
        referenceAmount:  toNumber(pick(c, 'reference_amount', 'referenceAmount', 0))
      });
    });

  // Trust server-reported counts when present, otherwise recompute
  // from the candidate list so the summary card never disagrees with
  // the table.
  const hasServerCounts =
    typeof preview.postableCount === 'number' ||
    typeof preview.blockedCount  === 'number' ||
    typeof preview.skippedCount  === 'number';

  let postableCount = toNumber(preview.postableCount);
  let blockedCount  = toNumber(preview.blockedCount);
  let skippedCount  = toNumber(preview.skippedCount);

  if (!hasServerCounts) {
    postableCount = 0; blockedCount = 0; skippedCount = 0;
    for (const c of candidates) {
      if      (c.action === ACTION_POST)               postableCount += 1;
      else if (c.action === ACTION_DUPLICATE_BLOCKED)  blockedCount  += 1;
      else if (c.action === ACTION_SKIP_NON_STOCKABLE) skippedCount  += 1;
    }
  }

  return {
    source,
    sourceHeader,
    candidates,
    postableCount,
    blockedCount,
    skippedCount
  };
}


// ─────────────────────────────────────────────────────────────────
// Posting payload builder — produces the body for
// POST /api/erp/inventory/inward/post/{grn|bill}/:sourceId.
//
// The backend accepts an OPTIONAL body — { remarks, postingDate,
// warehouseId } — and ignores keys it doesn't recognise. We send a
// stable shape so the API logs are easy to read; nullable / blank
// keys are stripped so an "empty" POST renders as `{}`, not as a
// noisy payload full of nulls.
// ─────────────────────────────────────────────────────────────────

export function buildInventoryPostingPayload(opts) {
  const o = opts && typeof opts === 'object' ? opts : {};
  const out = {};

  const remarks = pick(o, 'remarks', 'remarks', null);
  if (remarks !== null && remarks !== undefined) {
    const s = String(remarks).trim();
    if (s) out.remarks = s;
  }

  const postingDate = pick(o, 'posting_date', 'postingDate', null);
  if (postingDate !== null && postingDate !== undefined) {
    if (postingDate instanceof Date && !Number.isNaN(postingDate.getTime())) {
      out.postingDate = postingDate.toISOString().slice(0, 10);
    } else {
      const s = String(postingDate).trim();
      if (s) out.postingDate = s;
    }
  }

  const warehouseId = pick(o, 'warehouse_id', 'warehouseId', null);
  if (warehouseId !== null && warehouseId !== undefined) {
    const s = String(warehouseId).trim();
    if (s) out.warehouseId = s;
  }

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Item-type labels + tag colours. Colours align with the AntD Tag
// palette guidance in docs/23-antd-component-standards.md so the
// inventory grids match the rest of the control-plane chrome.
// ─────────────────────────────────────────────────────────────────

const ITEM_TYPE_LABELS = {
  GLASS:     'Glass',
  HARDWARE:  'Hardware',
  ACCESSORY: 'Accessory',
  PROFILE:   'Profile',
  UPVC:      'UPVC',
  ALUMINIUM: 'Aluminium',
  SERVICE:   'Service',
  JOBWORK:   'Jobwork',
  OTHER:     'Other'
};

const ITEM_TYPE_COLORS = {
  GLASS:     'blue',
  HARDWARE:  'purple',
  ACCESSORY: 'magenta',
  PROFILE:   'geekblue',
  UPVC:      'cyan',
  ALUMINIUM: 'gold',
  SERVICE:   'default',
  JOBWORK:   'default',
  OTHER:     'default'
};

export function itemTypeLabel(type) {
  if (!type) return '';
  const key = String(type).trim().toUpperCase();
  return ITEM_TYPE_LABELS[key] || key;
}

export function itemTypeColor(type) {
  if (!type) return 'default';
  const key = String(type).trim().toUpperCase();
  return ITEM_TYPE_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Movement-type labels — every ledger row carries one of three
// movement_type values; the operator sees a friendly label in the
// stock-ledger grid.
// ─────────────────────────────────────────────────────────────────

const MOVEMENT_TYPE_LABELS = {
  PURCHASE_INWARD:          'Purchase Inward',
  PURCHASE_INWARD_REVERSAL: 'Inward Reversal',
  ADJUSTMENT:               'Adjustment'
};

export function movementTypeLabel(type) {
  if (!type) return '';
  const key = String(type).trim().toUpperCase();
  return MOVEMENT_TYPE_LABELS[key] || key;
}


// ─────────────────────────────────────────────────────────────────
// Posted-status labels + tag colours.
// ─────────────────────────────────────────────────────────────────

const POSTED_STATUS_LABELS = {
  POSTED:    'Posted',
  REVERSED:  'Reversed',
  CANCELLED: 'Cancelled'
};

const POSTED_STATUS_COLORS = {
  POSTED:    'success',
  REVERSED:  'warning',
  CANCELLED: 'default'
};

export function postingStatusLabel(status) {
  if (!status) return '';
  const key = String(status).trim().toUpperCase();
  return POSTED_STATUS_LABELS[key] || key;
}

export function postingStatusColor(status) {
  if (!status) return 'default';
  const key = String(status).trim().toUpperCase();
  return POSTED_STATUS_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Classifier helpers
// ─────────────────────────────────────────────────────────────────

export function isNonStockable(itemType) {
  if (!itemType) return false;
  return NON_STOCKABLE_SET.has(String(itemType).trim().toUpperCase());
}


// ─────────────────────────────────────────────────────────────────
// Capability gate — drives the "Post Inward" CTA on the preview
// page. The backend remains the authority; this guard hides the CTA
// for operators who clearly can't succeed:
//
//   - source must be GRN or PURCHASE_BILL
//   - status must NOT be already-posted ('POSTED'); reversed /
//     cancelled rows can be re-posted (the backend will pick that
//     fight if it disagrees)
//   - postable count must be > 0
//   - if alreadyPostedCount equals postableCount + alreadyPostedCount
//     the whole source is already covered — hide the CTA
// ─────────────────────────────────────────────────────────────────

export function canPostInventorySource(opts) {
  const o = opts && typeof opts === 'object' ? opts : {};
  const sourceTypeRaw = pickStringUpper(o, 'sourceType', 'source_type');
  if (!SOURCE_TYPES_SET.has(sourceTypeRaw)) return false;

  const statusRaw = pickStringUpper(o, 'status', 'status');
  if (statusRaw === 'POSTED') return false;

  const postable = toNumber(o.postableCount);
  if (postable <= 0) return false;

  const already = toNumber(o.alreadyPostedCount);
  // Defensive: a negative count would otherwise enable the CTA.
  if (postable < 0 || already < 0) return false;

  return true;
}


// ─────────────────────────────────────────────────────────────────
// summarizePostingPreview — bucket the candidates by classifier
// action so the preview page can render an at-a-glance summary card
// without re-walking the list at every render.
//
// Tolerant: ignores non-array input, ignores rows without a string
// action, normalises action to upper-case.
// ─────────────────────────────────────────────────────────────────

export function summarizePostingPreview(preview) {
  const out = { postable: 0, skipped: 0, duplicates: 0, total: 0 };
  if (!preview || typeof preview !== 'object') return out;

  const list = Array.isArray(preview.candidates) ? preview.candidates : [];
  for (const c of list) {
    if (!c || typeof c !== 'object') continue;
    out.total += 1;
    const action = String(c.action || '').trim().toUpperCase();
    if      (action === ACTION_POST)               out.postable   += 1;
    else if (action === ACTION_SKIP_NON_STOCKABLE) out.skipped    += 1;
    else if (action === ACTION_DUPLICATE_BLOCKED)  out.duplicates += 1;
  }
  return out;
}


// ─────────────────────────────────────────────────────────────────
// Sanity references — keep the importer pattern at the top of this
// module honest. Touching these here keeps a future refactor from
// silently dropping an export. The 6P.1+6P.2 REQUEST_TYPES vs
// PR_REQUEST_TYPES bug is the model we are NOT repeating.
// ─────────────────────────────────────────────────────────────────
void MOVEMENT_TYPES_SET;
void POSTED_STATUSES_SET;
void KNOWN_TYPES_SET;
void GLASS_TYPES_SET;
void PROFILE_TYPES_SET;
void SIMPLE_TYPES_SET;
void LENGTH_UOMS_SET;
